import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"

/**
 * Converts a JavaScript Date object to a local time string
 * in the format "YYYY-MM-DD HH:mm:ss".
 *
 * @param {Date} date - The Date object to format.
 * @returns {string} - Formatted date string in local time.
 */

const formatDateToLocalString = (date) => {
  const pad = (n) => n.toString().padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const formatDateToWawString = (date) => {
  const parts = date.toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).replace(',', '')
    .replace(/\./g, '-')
    .split(' ')

  const [day, month, year] = parts[0].split('-')
  const time = parts[1]

  return `${year}-${month}-${day} ${time}`
}
const formatDateToLocalTimeString = (date) => {
  const pad = (n) => n.toString().padStart(2, '0')

  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const getCurrentTime = (noSeconds = false) => {
  const now = new Date()
  const hours = String(now.getHours()).padStart(2, "0")
  const minutes = String(now.getMinutes()).padStart(2, "0")
  const seconds = String(now.getSeconds()).padStart(2, "0")

  return noSeconds ? `${hours}:${minutes}` : `${hours}:${minutes}:${seconds}`
}

/**
 * Rounds a given time string to the nearest upper calendar slot duration.
 *
 * @param {string} timeStr - The time to round, in "HH:mm" format (e.g., "14:07").
 * @param {string} calendarSlotDurationStr - The duration of the calendar slot in the format "HH:mm:ss", e.g. "00:15:00".
 * @returns {string} - The rounded time in "HH:mm:ss" format, e.g. "14:15:00".
 */
function getTimeRoundedToSlot(timeStr, calendarSlotDurationStr) {
  const [hour, minute] = timeStr.split(':').map(Number)
  const [slotHours, slotMinutes, slotSeconds] = calendarSlotDurationStr.split(':').map(Number)
  const slotMs = (slotHours * 3600 + slotMinutes * 60 + slotSeconds) * 1000

  const baseDate = new Date()
  baseDate.setHours(hour, minute, 0, 0)

  const msSinceMidnight = baseDate.getHours() * 3600 * 1000 +
    baseDate.getMinutes() * 60 * 1000 +
    baseDate.getSeconds() * 1000 +
    baseDate.getMilliseconds()

  const roundedMs = Math.ceil(msSinceMidnight / slotMs) * slotMs
  const roundedDate = new Date(baseDate.setHours(0, 0, 0, 0) + roundedMs)

  return extractTime(roundedDate)
}

/**
 * Rounds the current time to the nearest calendar slot duration.
 *
 * @param {string} calendarSlotDurationStr - The duration of the calendar slot in the format "HH:mm:ss".
 * @param {number|null} addExtraMinutes - How many minutes to add to current time
 * @returns {string} - The rounded time in "HH:mm:ss" format.
 */
function getCurrentTimeRoundedToSlot(calendarSlotDurationStr = null, addExtraMinutes = 15) {
  if(!calendarSlotDurationStr) calendarSlotDurationStr = useCalendarStore().defaultSlotDuration
  const now = new Date()
  now.setMinutes(now.getMinutes() + addExtraMinutes)
  //else now.setMinutes(now.getMinutes())
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`

  return getTimeRoundedToSlot(currentTimeStr, calendarSlotDurationStr)
}

const delay = (time) => {
  return new Promise((resolve) => setTimeout(resolve, time))
  // use example
  // delay(1000).then(() => console.log('ran after 1 second1 passed'));
}

// Get the current date in the format YYYY.MM.DD or YYYY-MM-DD
const getCurrentDate = (format = ".") => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0') // Correct month formatting
  const day = String(now.getDate()).padStart(2, '0')
  if (format === '-') return `${year}-${month}-${day}`

  // return `${day}.${month}.${year}`
  return `${day}.${month}.${year}`
}

// Extract only the date from a Date object
const extractDate = (dateObj, format = ".") => {
  // If no dateObj is provided, or it's not a valid date, use the current date
  const dateToUse = dateObj instanceof Date && !isNaN(dateObj) ? dateObj : new Date()

  const year = dateToUse.getFullYear()
  const month = String(dateToUse.getMonth() + 1).padStart(2, '0')
  const day = String(dateToUse.getDate()).padStart(2, '0')
  if (format === '-') return `${year}-${month}-${day}`

  return `${day}.${month}.${year}`
}

// Extract only the time from a Date object
const extractTime = (dateObj) => {
  // If no dateObj is provided or it's not a valid date, use the current date
  const dateToUse = dateObj instanceof Date && !isNaN(dateObj) ? dateObj : new Date()

  const hours = String(dateToUse.getHours()).padStart(2, '0')
  const minutes = String(dateToUse.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}
// Extract only the time from a string like "2023-10-01 12:30"
const extractTimeFromDateString = (dateStr, showSeconds = false) => {
  const time = dateStr.split(' ')[1]

  return showSeconds ? time : time.slice(0, 5)
}
// Extract only the date from a string like "2023-10-01 12:30"
const extractDateFromString = (dateStr) => {
  return dateStr.split(' ')[0]
}

// Extract date and time
function extractDateTime(date, format = '-') {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  if (format === '.') return `${day}.${month}.${year} ${hours}:${minutes}`

  // Default format uses dots but in DD.MM.YYYY order
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

//Transform date string to Date object, supports 'DD.MM.YYYY' and 'YYYY-MM-DD'
const parseDateFromString = (dateStr, format = '-') => {
  let day, month, year

  if (format === '.') {
    [day, month, year] = dateStr.split('.')
  } else {
    [year, month, day] = dateStr.split('-')
  }

  const dateObj = new Date(`${year}-${month}-${day}`)

  if (isNaN(dateObj.getTime())) {
    console.error('Invalid Date', dateStr)

    return new Date()
  }

  return dateObj
}

/**
 * Combines a date string (YYYY-MM-DD) and time string (HH:MM) into a JavaScript Date object
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format
 * @returns {Date} - JavaScript Date object representing the combined date and time
 * @example
 * // Returns a Date object for May 7, 2025 at 12:30:00
 * combineDateAndTime("2025-05-07", "12:30")
 */
const combineDateAndTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    throw new Error('Both date and time strings are required')
  }

  // Create ISO format datetime string
  const combinedDateTime = `${dateStr}T${timeStr}:00`

  // Create a new Date object
  return new Date(combinedDateTime)
}
/**
 * Converts a time string in HH:MM format to minutes since midnight
 *
 * @param {string} timeStr - Time string in HH:MM format (e.g., "14:30")
 * @returns {number} - Minutes since midnight (e.g., 870 for "14:30")
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0
  const [hours, minutes] = timeStr.split(':').map(Number)

  return hours * 60 + minutes
}

/**
 * Calculates the difference in minutes between two times in "HH:MM" format.
 * If the end time is earlier than the start time, it assumes the end time is on the next day.
 *
 * @param {string} start - The start time, e.g. "23:00".
 * @param {string} end - The end time, e.g. "23:59".
 * @returns {number} - The difference in minutes between the two times.
 */
const getMinutesDifference = (start, end) => {
  const [startH, startM] = start.split(':').map(Number)
  const [endH, endM] = end.split(':').map(Number)

  const startTotal = startH * 60 + startM
  const endTotal = endH * 60 + endM

  return endTotal - startTotal
}


/**
 * Converts minutes since midnight to a time string in HH:MM format
 *
 * @param {number} minutes - Minutes since midnight (e.g., 870)
 * @returns {string} - Time string in HH:MM format (e.g., "14:30")
 */
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

// Extracts month and year from a date object
function extractMonthYear(date) {
  const { locale } = useI18n({ useScope: 'global' })
  const d = new Date(date)
  const month = d.toLocaleDateString(locale.value, { month: 'long' })
  const year = d.getFullYear()

  return `${month} ${year}`
}

// Format date to "Środa, 23 lipca 2025"
// 👉 Use it via v-html because of '&nbsp;' in the output
function formatFullDate(date, localeValue) {
  if (!date) return ''
  const d = new Date(date)
  let weekday = d.toLocaleDateString(localeValue, { weekday: 'long' })
  weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1)
  const day = d.getDate()
  const year = d.getFullYear()

  const monthGenitive = {
    'styczeń': 'stycznia',
    'luty': 'lutego',
    'marzec': 'marca',
    'kwiecień': 'kwietnia',
    'maj': 'maja',
    'czerwiec': 'czerwca',
    'lipiec': 'lipca',
    'sierpień': 'sierpnia',
    'wrzesień': 'września',
    'październik': 'października',
    'listopad': 'listopada',
    'grudzień': 'grudnia',
  }
  let month = d.toLocaleDateString(localeValue, { month: 'long' }).toLowerCase().trim()
  if (localeValue === 'pl') month = monthGenitive[month] || month

  return `${weekday}, ${day}&nbsp;${month} ${year}`
}

// Returns "21 — 27 lipiec 2025" for a given range object
function formatWeekRange({ start, end }) {
  const { locale } = useI18n({ useScope: 'global' })
  const startDate = new Date(start)
  const endDate = new Date(end)
  endDate.setDate(endDate.getDate() - 1)

  const dayStart = startDate.getDate()
  const dayEnd = endDate.getDate()
  const year = endDate.getFullYear()
  const month = endDate.toLocaleDateString(locale.value, { month: 'long' })

  return `${dayStart} - ${dayEnd} ${month} ${year}`
}

/**
 * Formats a duration given in minutes into a human-readable string, e.g. "1h 15min" or "45 min".
 *
 * @param {number} minutes - The number of minutes to format (e.g., 90).
 * @returns {string} - The formatted duration, e.g. "1h 30min" or "45 min".
 */
const formatTimeDuration = (minutes) => {
  if (!minutes) return '0 min'

  // Round the minutes to avoid decimal values
  const roundedMinutes = Math.floor(minutes)

  if (roundedMinutes < 60) return `${roundedMinutes} min`

  const h = Math.floor(roundedMinutes / 60)
  const m = roundedMinutes % 60

  if (m === 0) return `${h} h`

  return `${h} h ${m} min`
}

/**
 * Parses a time string ("HH:MM" or "HH:MM:SS") into total seconds since 00:00:00.
 *
 * @param {string} t - Time in 24-hour format, e.g. "09:30" or "09:30:15".
 * @returns {number} - Total seconds, or NaN if input is invalid.
 */
const parseTimeToSeconds = t => {
  if (typeof t !== 'string') return NaN
  const [hh = 0, mm = 0, ss = 0] = t.split(':').map(n => parseInt(n, 10))
  if ([hh, mm, ss].some(Number.isNaN)) return NaN

  return hh * 3600 + mm * 60 + ss
}

/**
 * Formats a number of seconds into a zero-padded time string ("HH:MM" or "HH:MM:SS").
 * Values are wrapped into a 24-hour clock (0–86399 seconds).
 *
 * @param {number} sec - Number of seconds to format.
 * @param {boolean} [withSeconds=false] - Whether to include seconds in the output.
 * @returns {string} - Formatted time string, or an empty string if input is invalid.
 */
const formatSecondsToTime = (sec, withSeconds = false) => {
  if (!Number.isFinite(sec)) return ''
  sec = ((sec % 86400) + 86400) % 86400 // wrap to 0..86399
  const pad = n => String(n).padStart(2, '0')
  const hh = Math.floor(sec / 3600)
  const mm = Math.floor((sec % 3600) / 60)
  const ss = Math.floor(sec % 60)
  const base = `${pad(hh)}:${pad(mm)}`

  return withSeconds ? `${base}:${pad(ss)}` : base
}


/**
 * Converts a duration to seconds. Accepts minutes (number or numeric string)
 * or a time string in "HH:MM" or "HH:MM:SS" format.
 *
 * @param {number|string} d - Duration in minutes (e.g., 90 or "90") or as "01:30" / "01:30:00".
 * @returns {number} - Duration in seconds, or NaN if input is invalid.
 */
const getDurationSeconds = d => {
  if (d == null) return NaN
  if (typeof d === 'number' && Number.isFinite(d)) return Math.round(d * 60)
  if (typeof d === 'string') {
    if (d.includes(':')) return parseTimeToSeconds(d)
    const m = parseFloat(d)
    if (Number.isFinite(m)) return Math.round(m * 60)
  }

  return NaN
}

/**
 * Adjusts a datetime string by a given number of minutes.
 * Works with formats "YYYY-MM-DD HH:mm" and "YYYY-MM-DD HH:mm:ss".
 *
 * @param {string} datetimeStr - Input datetime string.
 * @param {number} minutes - Number of minutes to add (or subtract if negative).
 * @returns {string} - Adjusted datetime string in "YYYY-MM-DD HH:mm:ss" format.
 */
function adjustDateTimeByMinutes(datetimeStr, minutes) {

  const isoString = datetimeStr.replace(' ', 'T')
  const date = new Date(isoString)

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format: *' + datetimeStr + '*, minutes: ' + minutes)
  }

  date.setMinutes(date.getMinutes() + minutes)


  const pad = n => String(n).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const mins = pad(date.getMinutes())
  const secs = pad(date.getSeconds())

  return `${year}-${month}-${day} ${hours}:${mins}:${secs}`
}

/**
 * Creates a date normalized to midnight (00:00:00.000)
 * Handles day offset correctly across month boundaries
 *
 * @param {number} dayOffset - Days to add/subtract (negative for past dates)
 * @returns {Date} - Date object at midnight
 */
const getDateAtMidnight = (dayOffset = 0) => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)

  if (dayOffset !== 0) {
    now.setDate(now.getDate() + dayOffset)
  }

  return now
}

/**
 * Human-readable distance between a date and today, in both directions: "pojutrze", "jutro",
 * "dzisiaj", "wczoraj", "za 3 dni", "5 dni temu", "za 2 tygodnie", "4 miesiące temu"
 * (and the English equivalents).
 *
 * Unit steps up with the distance: days up to 2 weeks, weeks up to 12, months beyond that.
 * Returns '' only for an invalid date.
 *
 * @param {Date|string} date - The date to describe.
 * @param {string} [locale='pl'] - i18n locale, e.g. 'pl' or 'en'.
 * @returns {string} - Localised phrase, or '' when the date cannot be parsed.
 */
const formatRelativeDay = (date, locale = 'pl') => {
  const target = new Date(date)
  if (isNaN(target.getTime())) return ''

  target.setHours(0, 0, 0, 0)

  // Calendar-day difference, so 23:00 yesterday vs 01:00 today is still "wczoraj"
  const days = Math.round((target - getDateAtMidnight()) / 86400000)
  const distance = Math.abs(days)

  // Intl handles the wording per direction and the Polish plural forms (2 tygodnie / 5 tygodni)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (distance < 14) return rtf.format(days, 'day')
  if (distance < 84) return rtf.format(Math.round(days / 7), 'week')

  return rtf.format(Math.round(days / 30.44), 'month')
}

/**
 * Adds a given number of minutes to a time string.
 * Works with format "HH:mm".
 *
 * @param {string} timeStr - Input time string (e.g. "09:45").
 * @param {number} minutesToAdd - Number of minutes to add (can be negative).
 * @returns {string} - Adjusted time string in "HH:mm" format.
 */
const addMinutesToTime = (timeStr, minutesToAdd) => {
  const [hours, minutes] = timeStr.split(':').map(Number)

  const totalMinutes = hours * 60 + minutes + minutesToAdd
  const newHours = Math.floor(totalMinutes / 60) % 24
  const newMinutes = ((totalMinutes % 60) + 60) % 60

  const hh = String((newHours + 24) % 24).padStart(2, '0')
  const mm = String(newMinutes).padStart(2, '0')

  return `${hh}:${mm}`
}

/**
 * Returns current date and time in format "2026_11_23-12_45"
 */
const getCurrentDateTimeStamp = () => {
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')

  return `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}-${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`
}

/**
 * Check if time is in valid format
 * eg.:
 * isValidTime('1:20')   // true
 * isValidTime('01:20')  // true
 * isValidTime('10:20')  // true
 * isValidTime('23:59')     // true
 * isValidTime('23:59:59')  // true
 * isValidTime('00:00:00')  // true
 * isValidTime('24:00')     // false
 * isValidTime('12:60')     // false
 * isValidTime('23:59:60')  // false
 * isValidTime('99:99')     // false
 * isValidTime('abc')       // false
 */
const isValidTime = (value) => {
  if (typeof value !== 'string') return false

  return /^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(value)
}

export {
  getCurrentTime,
  delay,
  getCurrentDate,
  extractDate,
  extractTime,
  extractTimeFromDateString,
  extractDateFromString,
  extractDateTime,
  parseDateFromString,
  combineDateAndTime,
  formatDateToLocalString,
  timeToMinutes,
  minutesToTime,
  getMinutesDifference,
  formatDateToWawString,
  formatDateToLocalTimeString,
  getCurrentTimeRoundedToSlot,
  formatFullDate,
  formatWeekRange,
  extractMonthYear,
  formatTimeDuration,
  formatSecondsToTime,
  parseTimeToSeconds,
  getDurationSeconds,
  adjustDateTimeByMinutes,
  getDateAtMidnight,
  formatRelativeDay,
  addMinutesToTime,
  getCurrentDateTimeStamp,
  isValidTime,
}
